import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, MapPin, Heart, Phone, FileText, MessageCircle, Star } from 'lucide-react';
import ContactModal from './ContactModal';
import { useSiteData } from '@/context/SiteDataContext';

const Header = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isDestinationsDropdownOpen, setIsDestinationsDropdownOpen] = useState(false);
  const [hoveredNav, setHoveredNav] = useState(null);
  const { siteSettings, destinations } = useSiteData();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { icon: Home, label: 'Home', href: '/' },
    { icon: MapPin, label: 'Destinations', href: '/destinations', hasDropdown: true },
    { icon: Star, label: 'Testimonials', href: '#testimonials' },
    { icon: Phone, label: 'Contact Us', href: '#contact', isModal: true },
    { icon: FileText, label: 'Terms', href: '/terms' }
  ];

  const handleWhatsApp = () => {
    const message = encodeURIComponent("Hi! I want to plan an international family holiday. Can you help me?");
    window.open(`https://wa.me/${siteSettings.whatsappNumber.replace('+', '')}?text=${message}`, '_blank');
  };

  const getSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-');
  };

  const isActiveRoute = (href) => {
    if (href.startsWith('#')) return false;
    return href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);
  };

  const scrollToSection = (e, href) => {
    e.preventDefault();
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        setIsMobileMenuOpen(false);
      }
    }
  };

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`header-nav ${isScrolled ? 'header-scrolled' : ''}`}
    >
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="header-logo">
          <img
            src="/newlogo.jpeg"
            alt="Elite Experiences Logo"
            className="logo-image"
          />
          <span className="header-wordmark display-heading">
            Elite<span className="gradient-text-rich">Experiences</span>
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="desktop-nav" onMouseLeave={() => setHoveredNav(null)}>
          {menuItems.map((item, index) => {
            const isActive = isActiveRoute(item.href);
            const showPill = hoveredNav === index || (hoveredNav === null && isActive);

            return (
              <div
                key={index}
                className="nav-item-slot"
                onMouseEnter={() => setHoveredNav(index)}
              >
                {showPill && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="nav-pill-highlight"
                  />
                )}
                {item.isModal ? (
                  <button
                    onClick={() => setIsContactModalOpen(true)}
                    className={`nav-item ${showPill ? 'active' : ''}`}
                    data-testid="contact-us-nav"
                  >
                    <item.icon size={18} />
                    <span>{item.label}</span>
                  </button>
                ) : item.hasDropdown ? (
                  <div
                    className="nav-item-dropdown"
                    onMouseEnter={() => setIsDestinationsDropdownOpen(true)}
                    onMouseLeave={() => setIsDestinationsDropdownOpen(false)}
                  >
                    <Link to={item.href} className={`nav-item ${showPill ? 'active' : ''}`}>
                      <item.icon size={18} />
                      <span>{item.label}</span>
                      <i className="fa-solid fa-chevron-down" style={{ fontSize: '0.75rem', marginLeft: '0.25rem' }}></i>
                    </Link>
                    <AnimatePresence>
                      {isDestinationsDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.97 }}
                          transition={{ duration: 0.18, ease: 'easeOut' }}
                          className="destinations-dropdown"
                        >
                          {destinations.map((dest) => (
                            <Link
                              key={dest.id}
                              to={`/destinations/${getSlug(dest.name)}`}
                              className="dropdown-item"
                              onClick={() => setIsDestinationsDropdownOpen(false)}
                            >
                              <MapPin size={16} />
                              <span>{dest.name}</span>
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : item.href.startsWith('#') ? (
                  <a
                    href={item.href}
                    onClick={(e) => scrollToSection(e, item.href)}
                    className={`nav-item ${showPill ? 'active' : ''}`}
                  >
                    <item.icon size={18} />
                    <span>{item.label}</span>
                  </a>
                ) : (
                  <Link to={item.href} className={`nav-item ${showPill ? 'active' : ''}`}>
                    <item.icon size={18} />
                    <span>{item.label}</span>
                  </Link>
                )}
              </div>
            );
          })}
        </nav>

        {/* WhatsApp CTA */}
        <motion.button
          whileHover={{ y: -2, boxShadow: '0 14px 30px rgba(37, 211, 102, 0.35)' }}
          whileTap={{ scale: 0.97 }}
          onClick={handleWhatsApp}
          className="whatsapp-header-btn"
        >
          <MessageCircle size={20} />
          <span>Let's Plan on WhatsApp</span>
        </motion.button>

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <div className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="mobile-menu"
          >
            {menuItems.map((item, index) => (
              item.isModal ? (
                <button
                  key={index}
                  onClick={() => {
                    setIsContactModalOpen(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="mobile-nav-item"
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                </button>
              ) : item.href.startsWith('#') ? (
                <a
                  key={index}
                  href={item.href}
                  onClick={(e) => scrollToSection(e, item.href)}
                  className="mobile-nav-item"
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                </a>
              ) : (
                <Link
                  key={index}
                  to={item.href}
                  className="mobile-nav-item"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                </Link>
              )
            ))}
            <button onClick={handleWhatsApp} className="mobile-whatsapp-btn">
              <MessageCircle size={20} />
              <span>Let's Plan on WhatsApp</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </motion.header>
  );
};

export default Header;
